"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { ChevronDown, ChevronRight, Crown, X } from "lucide-react";
import { Card, CardBody } from "@/components/ui/Card";
import { ItemIcon } from "@/components/icons/ItemIcon";
import { SpecIcon } from "@/components/icons/SpecIcon";
import { ProbabilityPill } from "@/components/ProbabilityPill";
import { PageHeader } from "../_components/PageHeader";
import { useLootCache } from "@/lib/loot-cache";
import { useCharState, toggleExcluded } from "@/lib/state";
import { poolBreakdown } from "@/lib/engine";
import { raidInstanceRank, bossRank } from "@/lib/season-display";
import { CLASS_BY_ID, isItemEligibleForClass } from "@/lib/classes";
import { qualityTextClass, SLOT_LABEL } from "@/lib/wow";
import { cn } from "@/lib/utils";
import type { Pool } from "@/lib/types";

export default function PoolsPage() {
  const state = useCharState();
  const cache = useLootCache();
  const cls = CLASS_BY_ID[state.classID];

  const dungeons: Pool[] = useMemo(
    () => Object.values(cache.pools).filter((p) => !p.isRaid).sort((a, b) => a.label.localeCompare(b.label)),
    [cache],
  );

  // Group raid pools by instance, ordered per season-display config.
  // Each entry is [instanceName, pools-in-encounter-order].
  const raidGroups: [string, Pool[]][] = useMemo(() => {
    const byInstance = new Map<string, Pool[]>();
    for (const p of Object.values(cache.pools)) {
      if (!p.isRaid) continue;
      if (!byInstance.has(p.instanceName)) byInstance.set(p.instanceName, []);
      byInstance.get(p.instanceName)!.push(p);
    }
    // Sort bosses within each instance by encounter order.
    for (const [name, pools] of byInstance) {
      pools.sort((a, b) => {
        const ra = bossRank(name, a.bossName);
        const rb = bossRank(name, b.bossName);
        if (ra !== rb) return ra - rb;
        return (a.label || "").localeCompare(b.label || "");
      });
    }
    // Sort instances by configured raid order.
    return Array.from(byInstance.entries()).sort(([a], [b]) => {
      const ra = raidInstanceRank(a);
      const rb = raidInstanceRank(b);
      if (ra !== rb) return ra - rb;
      return a.localeCompare(b);
    });
  }, [cache]);

  const totalExcluded = useMemo(
    () => Object.values(state.exclusions).reduce((s, row) => s + Object.keys(row).length, 0),
    [state.exclusions],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="POOLS & EXCLUSIONS"
        title="Loot pools"
        subtitle={`${dungeons.length} dungeons, ${raidGroups.length} raid${raidGroups.length === 1 ? "" : "s"}, ${totalExcluded} items excluded`}
      />

      <section>
        <p className="eyebrow mb-3">Mythic+ Dungeons</p>
        <div className="space-y-2">
          {dungeons.map((p) => <PoolCard key={p.id} pool={p} classID={state.classID} />)}
        </div>
      </section>

      {raidGroups.map(([instanceName, pools], idx) => (
        <section key={instanceName} className={idx > 0 ? "mt-8" : undefined}>
          <p className="eyebrow mb-3 inline-flex items-center gap-2">
            <Crown className="h-3 w-3 text-quality-epic" /> {instanceName}
            <span className="text-fg-subtle">|</span>
            <span className="text-fg-muted font-mono tabular normal-case tracking-normal">
              {pools.length} {pools.length === 1 ? "boss" : "bosses"}
            </span>
          </p>
          <div className="space-y-2">
            {pools.map((p) => (
              <PoolCard key={p.id} pool={p} classID={state.classID} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function PoolCard({ pool, classID }: { pool: Pool; classID: number }) {
  const [open, setOpen] = useState(false);
  const state = useCharState();
  const cache = useLootCache();
  const breakdown = useMemo(
    () => poolBreakdown(pool.id, cache, state),
    [pool.id, cache, state],
  );
  const cls = CLASS_BY_ID[classID];
  const playedRow = breakdown.find((b) => b.specID === state.playedSpecID);
  const excludedCount = Object.keys(state.exclusions[pool.id] ?? {}).length;

  // Hide items no spec of the user's class can equip. A cloth piece in a
  // Druid pool isn't "off-spec" — it's a different-class item entirely
  // and the bonus-roll loot table would never offer it. Computed once
  // per render; the engine math (poolBreakdown) does its own per-spec
  // filter and is unaffected by this UI-only change.
  const visibleItemIDs = useMemo(() => {
    return pool.itemOrder.filter((itemID) => {
      const item = cache.items[itemID];
      if (!item) return false;
      return isItemEligibleForClass(item.eligibleSpecs, classID);
    });
  }, [pool.itemOrder, cache.items, classID]);

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3.5 text-left hover:bg-surface-2/40 transition-colors"
      >
        <div className="flex items-center gap-4">
          {open ? (
            <ChevronDown className="h-4 w-4 text-fg-subtle shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-fg-subtle shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-fg truncate">{pool.label}</p>
            <p className="text-[11px] text-fg-subtle font-mono uppercase tracking-wider mt-0.5">
              {visibleItemIDs.length} items
              {excludedCount > 0 && ` · ${excludedCount} excluded`}
            </p>
          </div>
          {/* All-spec breakdown chips */}
          <div className="hidden md:flex items-center gap-1.5">
            {breakdown.map((b) => (
              <span
                key={b.specID}
                className={cn(
                  "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-xs",
                  "border border-border bg-surface-2",
                  b.hits === 0 && "opacity-30",
                )}
                title={`${b.specName}: ${b.hits}/${b.poolSize} = ${(b.p * 100).toFixed(0)}%`}
              >
                <SpecIcon specID={b.specID} size={14} />
                <span className="text-[10px] font-mono tabular text-fg">
                  {(b.p * 100).toFixed(0)}%
                </span>
              </span>
            ))}
          </div>
          {playedRow && playedRow.hits > 0 ? (
            <ProbabilityPill p={playedRow.p} hits={playedRow.hits} poolSize={playedRow.poolSize} />
          ) : (
            <span className="font-mono text-xs text-fg-subtle">—</span>
          )}
        </div>
      </button>

      {open && (
        <CardBody className="border-t border-border bg-surface-2/30 p-0">
          <ul className="divide-y divide-border">
            {visibleItemIDs.map((itemID) => {
              const item = cache.items[itemID];
              if (!item) return null;
              const excluded = !!state.exclusions[pool.id]?.[itemID];
              const eligibleForPlayed = item.eligibleSpecs.includes(state.playedSpecID);
              return (
                <li key={itemID} className={cn(excluded && "opacity-40")}>
                  <div className="px-5 py-2 flex items-center gap-3 hover:bg-surface-2/40 transition-colors">
                    <ItemIcon iconUrl={item.iconUrl} iconName={item.iconName} size={28} quality={item.quality} />
                    <Link
                      href={`/items/${item.id}`}
                      className="min-w-0 flex-1 group"
                    >
                      <p className={cn(
                        "text-sm truncate",
                        qualityTextClass(item.quality),
                        excluded && "line-through",
                        "group-hover:underline",
                      )}>
                        {item.name}
                      </p>
                      <p className="text-[11px] text-fg-subtle font-mono uppercase tracking-wider mt-0.5">
                        {SLOT_LABEL[item.slot] ?? item.slot}
                        {item.bossName && pool.isRaid === false ? ` · ${item.bossName}` : ""}
                        {item.isToken && " · Tier Token"}
                        {!eligibleForPlayed && " · off-spec"}
                      </p>
                    </Link>
                    <button
                      type="button"
                      onClick={() => toggleExcluded(pool.id, itemID)}
                      className={cn(
                        "px-2 h-7 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider rounded-xs border transition-colors",
                        excluded
                          ? "border-danger/40 bg-danger/10 text-danger hover:bg-danger/20"
                          : "border-border text-fg-subtle hover:border-border-strong hover:text-fg",
                      )}
                    >
                      {excluded ? <><X className="h-3 w-3" /> Excluded</> : "Mark rolled"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </CardBody>
      )}
    </Card>
  );
}
